import { useSDUIActionsHandler } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/ActionHandler/useSDUIActionsHandler";
import { logSDUIEvent } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/EventLogging/logSDUIEvent";
import {
  ActionLoggingEventParams,
  ComponentLoggingEventParams,
  LoggingEventParams,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/types";
import { getRetailer } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getRetailer";
import { trackCategoriesLayoutElementCtaClick } from "@faire/web-api--source/events/categories/click/layoutElementCta";
import { trackCategoriesLayoutElementItemClick } from "@faire/web-api--source/events/categories/click/layoutElementItem";
import { trackFlashSalesWarehouseSaleHomepageCarouselClick } from "@faire/web-api--source/events/flashSales/click/warehouseSaleHomepageCarousel";
import { trackHomeFeedLayoutElementCtaClick } from "@faire/web-api--source/events/homeFeed/click/layoutElementCta";
import { trackHomeFeedLayoutElementItemClick } from "@faire/web-api--source/events/homeFeed/click/layoutElementItem";
import { DisplayPageType } from "@faire/web-api--source/indigofair/data/DisplayPageType";
import { IAction } from "@faire/web-api--source/indigofair/data/IAction";
import { useCallback } from "react";

export type SDUIOnClickHandlerEventOptions = {
  logSDUIClickEvent: boolean;
  // TODO: https://fairewholesale.atlassian.net/browse/FD-217518
  // The logLegacyClickEvent option should be removed from the useSDUIOnClickHandler
  // once all layout element surfaces have been migrated over to SDUI
  // and data pipelines and dashboards no longer need the layout element events
  logLegacyClickEvent?: boolean;
  /**
   * Disabled by default.
   * When enabled, do not trigger click events on this SDUIOnClickHandler
   * when it has already been handled by a nested SDUIOnClickHandler.
   * This may prevent issues like the triggering of duplicate SDUI events on
   * parent tiles/components if there are nested `SDUIOnClickHandler`s
   * that each have logging or actions associated with them.
   * E.g. logging click events for a tile when just clicking text inside it
   *
   * See https://github.com/Faire/frontend/pull/18348.
   *
   * TODO: should be moved to backend so they control what events they are getting back
   */
  preventHandlingNestedClickEvents?: boolean;
};

export function useSDUIOnClickHandler(
  actions: IAction[],
  loggingData: LoggingEventParams,
  eventOptions: SDUIOnClickHandlerEventOptions = {
    logSDUIClickEvent: true,
    logLegacyClickEvent: false,
    preventHandlingNestedClickEvents: false,
  }
): (e?: React.MouseEvent<HTMLElement>) => void {
  const actionsHandler = useSDUIActionsHandler(actions, loggingData);

  return useCallback(
    (e?: React.MouseEvent<HTMLElement>) => {
      const originalEventTarget = e?.target as HTMLElement | undefined;
      if (
        eventOptions.preventHandlingNestedClickEvents &&
        originalEventTarget?.dataset?.sduiHandled
      ) {
        return;
      }

      actionsHandler();

      if (
        "component_type" in loggingData &&
        loggingData.component_type === "WAREHOUSE_SALE_CAROUSEL"
      ) {
        const retailerToken = getRetailer()?.token;
        if (retailerToken) {
          trackFlashSalesWarehouseSaleHomepageCarouselClick(retailerToken);
        }
      }

      if (eventOptions.logSDUIClickEvent) {
        logSDUIEvent("CLICK", loggingData);
      }

      if (
        eventOptions.logLegacyClickEvent &&
        "component_name" in loggingData &&
        loggingData.component_name
      ) {
        fireLegacyLayoutClickEvent(loggingData);
      }

      if (originalEventTarget) {
        originalEventTarget.dataset.sduiHandled = "true";
      }
    },
    [actionsHandler, loggingData, eventOptions]
  );
}

const fireLegacyLayoutClickEvent = (
  loggingData: ComponentLoggingEventParams | ActionLoggingEventParams
) => {
  if (loggingData.view_display_page === DisplayPageType.HOMEPAGE) {
    if (
      "action_instance_identifier" in loggingData &&
      loggingData.action_instance_identifier
    ) {
      trackHomeFeedLayoutElementItemClick(
        loggingData.component_name ?? "",
        loggingData.request_id ?? "",
        loggingData.action_instance_identifier
      );
    } else {
      trackHomeFeedLayoutElementCtaClick(
        loggingData.component_name ?? "",
        loggingData.request_id ?? ""
      );
    }
  }
  if (loggingData.view_display_page === DisplayPageType.CATEGORY_PAGE) {
    if (
      "action_instance_identifier" in loggingData &&
      loggingData.action_instance_identifier
    ) {
      trackCategoriesLayoutElementItemClick(
        loggingData.component_name ?? "",
        loggingData.request_id ?? "",
        loggingData.action_instance_identifier
      );
    } else {
      trackCategoriesLayoutElementCtaClick(
        loggingData.component_name ?? "",
        loggingData.request_id ?? ""
      );
    }
  }
};
